export interface PageMeta {
  /** Text shown in the browser tab and search results */
  title: string
  /** Search-result summary (meta description) */
  description: string
}

export const SITE_NAME = 'New River Systems'

/**
 * Meta title + description for every page, keyed by route.
 * Edit any entry to customize that page's header metadata.
 */
export const pageMeta: Record<string, PageMeta> = {
  '/': {
    title: 'New River Systems | Federal IT, ServiceNow & ICAM Solutions',
    description:
      'Service-Disabled Veteran-Owned Small Business delivering ServiceNow, identity and access management, AI, and program management services to DoD and federal agencies since 2007.',
  },
  '/services': {
    title: 'Services | New River Systems',
    description:
      'Explore New River Systems services: ServiceNow, Identity & Access Management, Program & Project Management, and AI & Intelligent Automation.',
  },
  '/services/ai': {
    title: 'AI & Intelligent Automation | New River Systems',
    description:
      'Enterprise AI strategy, ServiceNow GenAI, virtual agents, and agentic workflows for federal and enterprise organizations.',
  },
  '/services/identity-access-management': {
    title: 'Identity & Access Management (ICAM) | New River Systems',
    description:
      'FICAM-aligned identity and access management, Zero Trust, PKI, and identity governance solutions for federal agencies.',
  },
  '/services/identity-access-management/capabilities': {
    title: 'ICAM Capabilities | New River Systems',
    description:
      'Identity governance, privileged access, authentication, PKI, and compliance capabilities from New River Systems.',
  },
  '/services/identity-access-management/resources': {
    title: 'ICAM Resources | New River Systems',
    description:
      'Guides, checklists, and resources for federal identity, credential, and access management programs.',
  },
  '/services/program-and-project-management': {
    title: 'Program & Project Management | New River Systems',
    description:
      'Agile, traditional, and PMO program management for federal IT modernization and mission-critical programs.',
  },
  '/services/program-and-project-management/agile': {
    title: 'Agile Program Management | New River Systems',
    description:
      'Scrum, SAFe, and Agile delivery leadership for federal IT programs.',
  },
  '/services/program-and-project-management/pmo': {
    title: 'PMO Establishment & Operations | New River Systems',
    description:
      'Stand up, optimize, and operate Program Management Offices for federal agencies and enterprises.',
  },
  '/services/servicenow': {
    title: 'ServiceNow Governance & Implementation | New River Systems',
    description:
      'ServiceNow platform governance, implementation, and secure on-premise expertise for federal agencies.',
  },
  '/services/servicenow/services': {
    title: 'ServiceNow Services | New River Systems',
    description:
      'ServiceNow advisory, implementation, platform ownership, and managed services from New River Systems.',
  },
  '/services/servicenow/secure-on-premise': {
    title: 'Secure On-Premise ServiceNow | New River Systems',
    description:
      'Governance and cross-instance sync expertise for secure, on-premise ServiceNow environments.',
  },
  '/services/servicenow/thought-leadership': {
    title: 'ServiceNow Thought Leadership | New River Systems',
    description:
      'Articles and insights on ServiceNow governance, platform ownership, and federal platform strategy.',
  },
  '/services/servicenow/calculator': {
    title: 'ServiceNow Platform Risk Calculator | New River Systems',
    description:
      'Free self-assessment to score your ServiceNow platform risk across complexity, upgrades, integrations, and governance.',
  },
  '/services/servicenow/case-studies': {
    title: 'ServiceNow Case Studies | New River Systems',
    description:
      'Results from New River Systems ServiceNow engagements with federal and enterprise customers.',
  },
  '/resources/cross-instance-sync-checklist': {
    title: 'Cross-Instance Sync Risk Checklist | New River Systems',
    description:
      'A 10-question self-assessment to gauge the risk of your ServiceNow cross-instance sync process.',
  },
  '/about-us': {
    title: 'About Us | New River Systems',
    description:
      'Learn about New River Systems, a Service-Disabled Veteran-Owned Small Business serving federal agencies since 2007.',
  },
  '/about-us/our-team': {
    title: 'Our Team | New River Systems',
    description: 'Meet the leadership team at New River Systems.',
  },
  '/about-us/partners': {
    title: 'Partners | New River Systems',
    description:
      'Technology partners including ServiceNow, SAP, IBM, SailPoint, Saviynt, and more.',
  },
  '/about-us/certifications': {
    title: 'Certifications & Compliance | New River Systems',
    description:
      'ISO 9001:2015, ISO/IEC 27001:2022, CMMI Level 3, and CMMC Level 2 certifications held by New River Systems.',
  },
  '/about-us/community': {
    title: 'Community Impact | New River Systems',
    description:
      'How New River Systems supports veterans, STEM education, and the organizations serving our communities.',
  },
  '/about-us/contract-vehicles': {
    title: 'Contract Vehicles | New River Systems',
    description:
      'Contract vehicles held by New River Systems, including DLA JETS 2.0, GSA MAS, Alliant SB, DIA SITE III, NGA EMERALD, NASA SEWP VI, and VETS 2.',
  },
  '/about-us/contract-vehicles/nasa-sewp-vi': {
    title: 'NASA SEWP VI Contract | New River Systems',
    description:
      'New River Systems is a NASA SEWP VI prime contract holder in Category B and Category C. Contract details, ordering, and contacts.',
  },
  '/careers': {
    title: 'Careers | New River Systems',
    description:
      'Current job openings and career opportunities at New River Systems.',
  },
  '/contact-us': {
    title: 'Contact Us | New River Systems',
    description:
      'Contact New River Systems for business development, careers, and general inquiries.',
  },
}

/** Used for any route without its own entry (e.g. unknown URLs). */
export const defaultMeta: PageMeta = pageMeta['/']
